import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MobileCenteredLayout } from '@/components/MobileCenteredLayout/MobileCenteredLayout';
import { OnboardingProgress } from '@/components/OnboardingProgress/OnboardingProgress';
import { trackParentalConsentEvent } from '@/helpers/analytics';
import { hasValidParentalConsentNavigationState } from '@/Pages/ParentalConsent/types/parentalConsentLocationState';
import { GuardianForm } from '@/Pages/ParentalConsent/components/GuardianForm';
import { MinorContextBanner } from '@/Pages/ParentalConsent/components/MinorContextBanner';
import { ParentalConsentCta } from '@/Pages/ParentalConsent/components/ParentalConsentCta';
import { ParentalConsentHeader } from '@/Pages/ParentalConsent/components/ParentalConsentHeader';
import { useParentalConsentForm } from '@/Pages/ParentalConsent/hooks/useParentalConsentForm';
import '@/Pages/ParentalConsent/ParentalConsentPage.sass';
import { WelcomeFooter } from '@/components/WelcomeFooter/WelcomeFooter';

const SIGNUP_PATH = '/signup';

export function ParentalConsentPage(): JSX.Element | null {
  const location = useLocation();
  const navigate = useNavigate();
  const hasValidNavigationState = hasValidParentalConsentNavigationState(location.state);

  useEffect(() => {
    if (!hasValidNavigationState) {
      void trackParentalConsentEvent({ name: 'parental_consent_guard_redirect' });
      navigate(SIGNUP_PATH, { replace: true });
      return;
    }
    void trackParentalConsentEvent({ name: 'parental_consent_page_view' });
  }, [hasValidNavigationState, navigate]);

  if (!hasValidNavigationState) {
    return null;
  }

  return <ParentalConsentPageContent />;
}

function ParentalConsentPageContent(): JSX.Element {
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const {
    minorData,
    form,
    fieldErrors,
    touched,
    isSubmitEnabled,
    isSubmitting,
    setField,
    blurField,
    submit,
    goBack,
  } = useParentalConsentForm();

  return (
    <MobileCenteredLayout>
      <Box
        component="main"
        className={
          prefersReducedMotion
            ? 'parental-consent-page parental-consent-page--reduced-motion'
            : 'parental-consent-page'
        }
        data-testid="parental-consent-page-main"
        data-motion={prefersReducedMotion ? 'reduced' : 'animated'}
      >
        <OnboardingProgress currentStep={4} />

        <ParentalConsentHeader />

        <MinorContextBanner fullName={minorData.fullName} />

        <GuardianForm
          form={form}
          fieldErrors={fieldErrors}
          touched={touched}
          setField={setField}
          blurField={blurField}
        />

        <ParentalConsentCta
          disabled={!isSubmitEnabled}
          isSubmitting={isSubmitting}
          onSubmit={submit}
          onBack={goBack}
        />

        {/* <ParentalConsentFooter /> */}
        <WelcomeFooter/>
      </Box>
    </MobileCenteredLayout>
  );
}
